import uniq from 'lodash/uniq';
import isNil from 'lodash/isNil';
import { IClientVarsService } from '../../../core/interfaces/client-vars-service.interface';
import { IOnlineApiService } from '../core/interfaces/online-api-service.interface';
import { getCookie } from 'steamer-browserutils';
import { IEventManager } from '@tencent/slide-sdk-model';

export interface IUserInfo {
    userId?: string;
    uid?: string;
    markName?: string;
    name?: string;
    userPic?: string;
    corpName?: string;
    anonymous?: boolean; // 是否隐身
}

/** [uid 标记] 游客模式，即未登录状态访问 */
const GUEST_TOKEN = 'g.';
/** [uid 标记] 参与者 */
const PARTICIPANT_TOKEN = 'p.';

export const isUserInfoComplete = (userInfo?: IUserInfo): userInfo is Required<IUserInfo> => !isNil(userInfo?.name)
    && !isNil(userInfo?.userPic);

/** convent userId to uid; e.g. 'g.10086' -> '10086' */
export const getRealUid = (userId: string) => userId.replace(GUEST_TOKEN, '').replace(PARTICIPANT_TOKEN, '');

/**
 * 用户管理，主要提供用户基础信息查询
 */
export class UserManager {
    /** userInfo 接口数据缓存 */
    private requestCache = new Map<string, IUserInfo>();
    private getUserInfosPromise?: Promise<unknown>;

    public constructor(
        private clientVars: IClientVarsService,
        private apiService: IOnlineApiService,
        private sdkEventManager: IEventManager,
    ) {
        this.sdkEventManager.socketEvents.onUserEnter(this.onUserEnter.bind(this));
    }

    /**
     * 根据uid 从缓存中获取用户信息 包含昵称、头像、uid
     * @param uid 需要获取用户信息的uid
     */
    public getUserInfoFromCache(uid: string): IUserInfo | undefined {
        return this.requestCache.get(uid.toString());
    }

    /**
     * 根据uids 获取(cache 优先)批量用户信息 包含昵称、头像、uid
     * // TODO: 合并函数，移除 callback; 目前为了保持兼容暂未施工
     * @param uids 需要获取的uid array
     * @param callBack userInfos 用户数据数组，若接口失败，仍返回命中缓存的部分结果；error 错误信息
     */
    public async getUserInfosFromUids(uids: string[]) {
        const userInfosFromCache: IUserInfo[] = [];
        const newUserIds: string[] = [];
        uniq(uids).forEach((uid) => {
            const userInfo = this.requestCache.get(uid.toString());
            userInfo ? userInfosFromCache.push(userInfo) : newUserIds.push(uid);
        });
        if (!newUserIds.length) return userInfosFromCache;
        const newUserInfoFromServer = await this.getAndCacheUserInfo(newUserIds);
        return [...userInfosFromCache, ...newUserInfoFromServer];
    }

    /**
     * 更新缓存的用户信息
     * @param userInfos
     */
    public async updateUserInfoCache(): Promise<void> {
        const result = await this.getUserInfosByPadID();
        const userInfos = result.userInfos as (IUserInfo & {reqUid: string})[];
        userInfos?.forEach(({ reqUid, markName = '', userPic = '', corpName = '', anonymous = false }) => {
            const userInfo = { uid: reqUid, markName, userPic, corpName, anonymous };
            this.requestCache.set(reqUid.toString(), userInfo);
        });
    }

    /** 增加用户 */
    private onUserEnter(userInfo: IUserInfo) {
        // userId 不存在，相当于没有添加用户
        if (!userInfo.userId) {
            return;
        }
        // 如果用户信息已经缓存，不再二次更新
        const uid = getRealUid(userInfo.userId);
        if (this.requestCache.has(uid.toString())) {
            return;
        }
        this.getAndCacheAllUserInfos();
    }

    private async getAndCacheUserInfo(newUserIds: Array<string>) {
        const { userInfos = [] } = await this.getUserInfosByPadID();
        return userInfos
            .filter(userInfo => newUserIds.find(uid => userInfo.uid === uid || userInfo.reqUid === uid))
            .map(({ reqUid, markName = '', userPic = '', corpName = '' }) => {
                const userInfo = { uid: reqUid, markName, userPic, corpName };
                // 确保 cacheMap 的所有 key 都是 string;
                this.requestCache.set(reqUid.toString(), userInfo);
                return userInfo;
            });
    }


    private getAndCacheAllUserInfos() {
        // 此接口全量获取数据，若已经有正在执行的请求，就不再二次发送
        if (this.getUserInfosPromise) {
            return;
        }
        this.getUserInfosPromise = this.getUserInfosByPadID().then((result) => {
            const userInfos = result.userInfos as (IUserInfo & {reqUid: string})[];
            userInfos?.forEach(({ reqUid, markName = '', userPic = '', corpName = '', anonymous = false }) => {
                const userInfo = { uid: reqUid, markName, userPic, corpName, anonymous };
                this.requestCache.set(reqUid.toString(), userInfo);
            });
            this.getUserInfosPromise = undefined;
        });
    }

    /**
     * 获取房间层前count个用户的信息.
     * 后台逻辑是先从房间层拉全量uid再取count拉详情，全量uid是没有排序的，命中率没法保证
     */
    private async getUserInfosByPadID() {
        const { globalPadId = '' } = this.clientVars.getClientVars();
        try {
            const res = await this.apiService.getUserInfosByPadID({
                bkn: this.getBkn() ?? 0,
                tag: globalPadId,
                globalPadID: globalPadId,
            });
            if (res.data.retcode !== 0) throw new Error('cgi error');
            return { userInfos: res.data.result.rsp, totalCount: res.data.count };
        } catch (error) {
            return {};
        }
    }

    private getBkn() {
        const t = getCookie('skey');
        let e = 0;
        let n = 5381;

        if (!t) {
            return;
        }

        for (let o = 0; o > e; ++e) {
            n += (n < 5) + t.charAt(e).charCodeAt(0);
        }
        return 2147483647 & n;
    }
}
